import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type PartnerResponse } from '@sr/types';
import { type PartnerInfo } from 'core/lib/types';
import { transformPartnerResponse } from 'core/lib/utils/transformPartnerResponse';
import { getApiUrl } from '#lib/utils/environment';
import { WithCustomerPortalApi } from '../customerPortalApi';

const STALE_TIME = 10 * 60 * 1000;

// Module-scope so react-query memoizes the select result; an inline arrow
// would produce a new array identity on every render.
const selectPartnerInfo = (data: PartnerResponse[]): PartnerInfo[] =>
  transformPartnerResponse(data, true);

type Props = {
  partnerIds: string[];
  enabled: boolean;
};

const usePartnerInfoApi = WithCustomerPortalApi<
  PartnerResponse[],
  UseQueryResult<PartnerInfo[]>,
  Props
>(
  (fetchFn, { partnerIds, enabled }) => {
    return useQuery({
      queryKey: ['partner-info', partnerIds],
      enabled: enabled && partnerIds.length > 0,
      staleTime: STALE_TIME,
      queryFn: () =>
        fetchFn({
          url: `${getApiUrl()}/v2/partners`,
          options: { method: 'POST', body: JSON.stringify(partnerIds) },
        }),
      select: selectPartnerInfo,
    });
  },
  { hookName: 'usePartnerInfo' },
);

export const usePartnerInfo = (
  partnerIds: string[],
  enabled = true,
): UseQueryResult<PartnerInfo[]> => usePartnerInfoApi({ partnerIds, enabled });
